import { Dialog } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import {
  Button,
  MobileStepper,
  Stepper,
  Step,
  StepButton,
  DialogTitle,
  IconButton,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Box } from "@mui/system";
import { useState } from "react";
import { HowToStep } from "./Constants";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import SmallDeviceSteps from "./HowToDialogContentChildren/SmallDeviceSteps";
import LargeDeviceSteps from "./HowToDialogContentChildren/LargeDeviceSteps";

type HowToDialogContentProps = {
  title: string;
  handleClose: () => void;
  isSmallDevice: boolean;
  howToSteps: Array<HowToStep>;
};

const renderStepper = (
  isSmallDevice: boolean,
  activeStep: number,
  maxSteps: number,
  isLastStep: boolean,
  labels: Array<string>,
  handleBack: () => void,
  handleNext: () => void,
  handleStep: (step: number) => void,
) => {
  const BackButton = () => {
    return (
      <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
        <KeyboardArrowLeft />
        Back
      </Button>
    );
  };

  const NextButton = () => {
    return (
      <Button
        size="small"
        onClick={handleNext}
        endIcon={isLastStep ? <DoneIcon /> : <KeyboardArrowRight />}
      >
        {isLastStep ? "Done" : "Next"}
      </Button>
    );
  };

  return (
    <Box width={"100%"}>
      {isSmallDevice ? (
        <MobileStepper
          steps={maxSteps}
          activeStep={activeStep}
          variant="text"
          position="static"
          backButton={<BackButton />}
          nextButton={<NextButton />}
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === "light"
                ? theme.palette.background.paper
                : theme.palette.grey[700],
          }}
        />
      ) : (
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <BackButton />
          <Stepper nonLinear activeStep={activeStep}>
            {labels.map((label, index) => (
              <Step key={label}>
                <StepButton onClick={() => handleStep(index)}>
                  {/* label here or nah? */}
                </StepButton>
              </Step>
            ))}
          </Stepper>
          <NextButton />
        </Box>
      )}
    </Box>
  );
};

const HowToDialogContent = ({
  title,
  handleClose,
  isSmallDevice,
  howToSteps,
}: HowToDialogContentProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const maxSteps = howToSteps.length;
  const isLastStep = activeStep === maxSteps - 1;
  const labels = howToSteps.map((howToStep) => howToStep.title);

  const handleNext = () => {
    if (isLastStep) {
      handleClose();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  const handleStep = (step: number) => {
    setActiveStep(step);
  };

  return (
    <>
      <DialogTitle display={"flex"} justifyContent={"space-between"}>
        {title}
        <Box flexShrink={0}>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <>
          {isSmallDevice ? (
            <SmallDeviceSteps
              activeStep={activeStep}
              handleStep={handleStep}
              howToSteps={howToSteps}
            />
          ) : (
            <LargeDeviceSteps activeStep={activeStep} howToSteps={howToSteps} />
          )}
        </>
      </DialogContent>
      <DialogActions>
        {renderStepper(
          isSmallDevice,
          activeStep,
          maxSteps,
          isLastStep,
          labels,
          handleBack,
          handleNext,
          handleStep,
        )}
      </DialogActions>
    </>
  );
};

type HowToDialogProps = {
  open: boolean;
  title: string;
  handleClose: () => void;
  howToSteps: Array<HowToStep>;
  isSmallDevice: boolean;
};

const HowToDialog = ({
  open,
  title,
  handleClose,
  howToSteps,
  isSmallDevice,
}: HowToDialogProps) => {
  return (
    <Dialog
      open={open}
      maxWidth="lg"
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            overflowY: "hidden !important",
            height: "100%",
            maxHeight: "950px",
          },
        },
      }}
      sx={{
        paddingX: { xs: 1, sm: 2, md: 2.5 },
        paddingY: { xs: 1, sm: 2 },
      }}
    >
      <HowToDialogContent
        title={title}
        handleClose={handleClose}
        isSmallDevice={isSmallDevice}
        howToSteps={howToSteps}
      />
    </Dialog>
  );
};

export default HowToDialog;
